import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWandMagicSparkles, faRightLeft,
  faCommentDots, faUser,
  faCircleExclamation, faArrowRight,
  faHourglassHalf, faLock,
  faChalkboardUser, faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import styles from "./GoogleWelcome.module.css";

/* Temporário: billing em configuração - desabilitar geração por IA */
const SELF_WORKOUT_ENABLED = true;

const resolveAvatarUrl = (avatarUrl, apiUrl, fallbackName = "User") => {
  if (!avatarUrl)
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(fallbackName)}&background=22c55e&color=fff`;
  if (/^https?:\/\//i.test(avatarUrl)) return avatarUrl;
  return `${(apiUrl || "").replace(/\/+$/, "")}/${String(avatarUrl).replace(/^\/+/, "")}`;
};



export default function GoogleWelcome({ user, apiUrl, onLogout, onOpenProfile, onAiWorkout, onMigrateWorkout, onContactProfessor, onOpenSupport, onBecomeProfessor, becomingProfessor }) {
  const [showLogout, setShowLogout]         = useState(false);

  const avatarSrc = resolveAvatarUrl(user?.avatar_url, apiUrl, user?.name || "User");
  const firstName = user?.name?.split(" ")[0] || "bem-vindo";

  /* M6 - badge de setup pendente — vale tanto pra quem entrou com Google
     quanto com Apple (nenhum dos dois nasce com senha própria) */
  const needsPassword = user?.auth_provider === "google" || user?.auth_provider === "apple";

  /* C3 - só exibe opção de professor quando há handler real */
  const hasContactProfessor = typeof onContactProfessor === "function";

  const handleLogout = () => {
    ["token", "user", "expiresAt"].forEach(k => localStorage.removeItem(k));
    typeof onLogout === "function" ? onLogout() : window.location.reload();
  };

  return (
    <div className={styles.page}>
      <div className={styles.bgGrid} />
      <div className={styles.glow} />

      {/* ── Topbar ── */}
      <header className={styles.topbar}>
        <img src="/brand/treinonexo-wordmark-white.svg" alt="TreinoNexo" className={`${styles.brand} ${styles.brandDark}`} />
        <img src="/brand/treinonexo-wordmark.svg" alt="TreinoNexo" className={`${styles.brand} ${styles.brandLight}`} />
        <div className={styles.topbarRight}>
          {/* M5 - sem lazy loading (acima do fold) */}
          <img src={avatarSrc} alt={user?.name} className={styles.topbarAvatar} referrerPolicy="no-referrer" />
          <div className={styles.topbarMeta}>
            <span className={styles.topbarName}>{user?.name || "Usuário"}</span>
            <span className={styles.topbarEmail}>{user?.email}</span>
          </div>
          {typeof onOpenProfile === "function" && (
            <button className={styles.outlineBtn} onClick={onOpenProfile} title={needsPassword ? "Complete seu cadastro - defina uma senha" : "Meu perfil"}>
              <FontAwesomeIcon icon={faUser} />
              <span className={styles.outlineBtnLabel}> Meu perfil</span>
              {/* M6 - badge de alerta quando não tem senha */}
              {needsPassword && (
                <span className={styles.setupBadge} aria-label="Cadastro incompleto">
                  <FontAwesomeIcon icon={faCircleExclamation} />
                </span>
              )}
            </button>
          )}
          <button className={styles.outlineBtn} onClick={() => setShowLogout(true)}>Sair</button>
        </div>
      </header>

      <main className={styles.main}>

        {/* Aviso de senha pendente vive só no badge do botão "Meu perfil" no
            topbar (needsPassword) — banner dedicado aqui ocupava espaço
            demais e gerava scroll na página, ver docs/07-issues.md. */}

        {/* ── HERO ── */}
        <section className={styles.hero}>
          <h1 className={styles.heroTitle}>
            Olá, {firstName}!{" "}
            <span className={styles.accent}>Tudo pronto para começar.</span>
          </h1>
          <p className={styles.heroSub}>
            Escolha como prefere começar -{" "}
            <em>leva menos de 2 minutos.</em>
          </p>

          <div className={styles.heroCtaRow}>
            {SELF_WORKOUT_ENABLED ? (
              <div className={styles.pathChoice}>
                <button type="button" className={`${styles.pathCard} ${styles.pathCardPrimary}`} onClick={onAiWorkout}>
                  <FontAwesomeIcon icon={faWandMagicSparkles} className={styles.pathCardIcon} />
                  <span className={styles.pathCardTitle}>Gerar novo treino personalizado para você</span>
                  <span className={styles.pathCardDesc}>
                    Responda algumas perguntas sobre objetivo, frequência e nível. Montamos um treino do zero pra você, de acordo com suas preferências.
                  </span>
                </button>
                {typeof onMigrateWorkout === "function" && (
                  <button type="button" className={styles.pathCard} onClick={onMigrateWorkout}>
                    <FontAwesomeIcon icon={faRightLeft} className={styles.pathCardIcon} />
                    <span className={styles.pathCardTitle}>Já treino, quero migrar meu treino atual</span>
                    <span className={styles.pathCardDesc}>
                      Descreva o treino que você já faz e a gente só organiza na sua ficha, sem redesenhar nada.
                    </span>
                  </button>
                )}
              </div>
            ) : (
              <>
                <button className={`${styles.primaryBtn} ${styles.heroCta} ${styles.heroCtaWait}`} disabled>
                  <FontAwesomeIcon icon={faHourglassHalf} /> Aguarde seu professor
                </button>
                <span className={styles.heroCtaLocked}>
                  <FontAwesomeIcon icon={faLock} /> Geração por IA em breve
                </span>
              </>
            )}
          </div>

        </section>


        {/* ── PRÓXIMOS PASSOS - orientação de fluxo, não pitch de produto ── */}
        {SELF_WORKOUT_ENABLED && (
          <section className={styles.nextStepsStrip}>
            <div className={styles.nextStep}>
              <span className={styles.nextStepNum}>01</span>
              <p className={styles.nextStepText}>
                Responda <strong>5 perguntas</strong> sobre objetivo, frequência e nível
              </p>
            </div>
            <FontAwesomeIcon icon={faArrowRight} className={styles.nextStepArrow} />
            <div className={styles.nextStep}>
              <span className={styles.nextStepNum}>02</span>
              <p className={styles.nextStepText}>
                Nós montamos seu treino completo - <strong>divisão de dias, exercícios e cargas</strong>
              </p>
            </div>
            <FontAwesomeIcon icon={faArrowRight} className={styles.nextStepArrow} />
            <div className={styles.nextStep}>
              <span className={styles.nextStepNum}>03</span>
              <p className={styles.nextStepText}>
                Você revisa, aprova e <strong>já pode treinar hoje</strong>
              </p>
            </div>
          </section>
        )}

        {/* ── ENQUANTO AGUARDA - exibido quando billing ainda não está ativo ── */}
        {!SELF_WORKOUT_ENABLED && (
          <section className={styles.block}>
            <div className={styles.blockHeader}>
              <span className={styles.tag}>Enquanto aguarda</span>
              <h2 className={styles.blockTitle}>Prepare-se para começar</h2>
            </div>
            <div className={styles.waitingSteps}>
              <div className={styles.waitingItem}>
                <span className={styles.waitingNum}>01</span>
                <div>
                  <strong className={styles.waitingTitle}>Complete seu perfil</strong>
                  <p className={styles.waitingDesc}>
                    Defina seu nome e, se entrou pelo Google,
                    crie uma senha para não perder o acesso à conta.
                  </p>
                  {typeof onOpenProfile === "function" && (
                    <button className={styles.textLink} onClick={onOpenProfile} style={{ marginTop: 6 }}>
                      Abrir perfil <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 11 }} />
                    </button>
                  )}
                </div>
              </div>
              <div className={styles.waitingItem}>
                <span className={styles.waitingNum}>02</span>
                <div>
                  <strong className={styles.waitingTitle}>Aguarde a ficha do seu professor</strong>
                  <p className={styles.waitingDesc}>
                    Seu professor está preparando um plano de treino personalizado para você.
                    Quando estiver pronto, ele aparecerá aqui no Dashboard.
                  </p>
                </div>
              </div>
              <div className={styles.waitingItem}>
                <span className={styles.waitingNum}>03</span>
                <div>
                  <strong className={styles.waitingTitle}>Treine e registre sua evolução</strong>
                  <p className={styles.waitingDesc}>
                    Com a ficha definida, você poderá registrar treinos, acompanhar progresso
                    e receber ajustes do professor ao longo do tempo.
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* M2 - stats band REMOVIDA (plataforma ≠ contexto do usuário autenticado) */}

        {/* M4 + C3 - professor como upgrade positivo, apenas quando há handler real */}
        {hasContactProfessor && (
          <section className={styles.block}>
            <div className={styles.blockHeader}>
              <span className={styles.tag}>Quer mais?</span>
              <h2 className={styles.blockTitle}>Amplie com um professor</h2>
            </div>
            <div className={styles.professorCard}>
              <p className={styles.professorCardDesc}>
                Com um professor vinculado, você tem treino personalizado e ajustado,
                acompanhamento da evolução, dieta IA e desafios em grupo - tudo em um só lugar.
              </p>
              <button className={`${styles.ghostBtn} ${styles.fullBtn}`} onClick={onContactProfessor}>
                <FontAwesomeIcon icon={faCommentDots} /> Falar com o professor
              </button>
            </div>
          </section>
        )}


        {/* ── Seção professor ── */}
        <section className={styles.block} style={{ marginTop: 8 }}>
          <div className={styles.blockHeader}>
            <span className={styles.tag}>Professores</span>
            <h2 className={styles.blockTitle}>Você é personal trainer ou professor?</h2>
          </div>
          <div className={styles.professorContactCard}>
            <p className={styles.professorContactDesc}>
              Personal trainers e professores têm acesso a ferramentas de gestão de alunos, criação de fichas com IA e acompanhamento de evolução em tempo real. Transforme sua conta em professor agora - é só um clique.
            </p>
            <div className={styles.professorContactBtns}>
              {typeof onBecomeProfessor === "function" && (
                <button
                  type="button"
                  className={`${styles.ghostBtn} ${styles.contactBtn} ${styles.contactBtnEmail}`}
                  onClick={onBecomeProfessor}
                  disabled={becomingProfessor}
                >
                  <FontAwesomeIcon
                    icon={becomingProfessor ? faSpinner : faChalkboardUser}
                    className={becomingProfessor ? styles.spin : undefined}
                  />
                  {becomingProfessor ? "Ativando..." : "Virar professor"}
                </button>
              )}
              <button
                type="button"
                className={`${styles.ghostBtn} ${styles.contactBtn}`}
                onClick={() => onOpenSupport?.()}
              >
                <FontAwesomeIcon icon={faCommentDots} />
                Dúvidas? Clique aqui
              </button>
            </div>
          </div>
        </section>

      </main>

      {/* ── Logout confirm ── */}
      {showLogout && (
        <div className={styles.overlay} onClick={() => setShowLogout(false)}>
          <div className={styles.logoutModal} onClick={e => e.stopPropagation()}>
            <h3>Sair da conta</h3>
            <p>Deseja sair da sua conta?</p>
            <div className={styles.logoutBtns}>
              <button className={styles.outlineBtn} onClick={() => setShowLogout(false)}>Cancelar</button>
              <button className={styles.dangerBtn} onClick={handleLogout}>Sair</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}